import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Table,
  Spin,
  message,
  Radio,
  Calendar,
  Modal,
  Typography,
  Button,
  Space,
  Tooltip,
  Popconfirm,
  Checkbox,
  Tag
} from 'antd';
import { CalendarOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { app } from '../../../service/serviceLMS';
import AdminHOC from "../../shared/HOC/AdminHOC";
import { useNavigate } from 'react-router-dom';
import FrequencyModal from './FrequencyModal';
import CustomBreadcrumb from '../../shared/breadcrumbs/CustomBreadCrumb';
import Back from '../../../assets/back-btn.svg';
import CustomTab from '../../shared/tab/TabComponent';
import CustomButton from '../../shared/button/CustomButton';
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const getGroupKey = (e) => {
  if (e.bundleId && e.groupId) return `bundle-${e.bundleId}-group-${e.groupId}`;
  if (e.bundleId && e.userId) return `bundle-${e.bundleId}-user-${e.userId}`;
  if (e.courseId && e.groupId) return `course-${e.courseId}-group-${e.groupId}`;
  if (e.courseId && e.userId) return `course-${e.courseId}-user-${e.userId}`;
  return `enrollment-${e.enrollmentId}`;
};

const FrequencyDashboard = ({ setHeader, setBreadCrumbs }) => {
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(true);
  const [dataSets, setDataSets] = useState({
    all: [],
    upcoming: [],
    completed: [],
    ongoing: [],
    inactive: []
  });
  const [metaMap, setMetaMap] = useState({});
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [selectionModalVisible, setSelectionModalVisible] = useState(false);
  const [availableEnrollments, setAvailableEnrollments] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [frequencyModalVisible, setFrequencyModalVisible] = useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState('users'); // 'users' or 'groups'
  const [contentFilter, setContentFilter] = useState('courses'); // 'courses' or 'bundles' 
  const navigate = useNavigate();

  const fetchMeta = async (enrollments) => {
    setLoadingMeta(true);
    const map = {};

    for (const e of enrollments) {
      const { courseId, bundleId, userId, groupId } = e;

      if (courseId && !map[courseId]) {
        const res = await app.get(`course/api/client-api/course/${courseId}`).catch(() => null);
        map[courseId] = res?.data?.data?.title || `Course ID ${courseId}`;
      }

      if (bundleId && !map[bundleId]) {
        const res = await app.get(`course/api/client-api/bundles/${bundleId}`).catch(() => null);
        map[bundleId] = res?.data?.data?.bundleName || `Bundle ID ${bundleId}`;
      }

      if (userId && !map[`user-${userId}`]) {
        const res = await app.get(`user/api/client-api/users/${userId}`).catch(() => null);
        const user = res?.data?.data;
        map[`user-${userId}`] =
          user?.firstName || user?.lastName
            ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
            : user?.username || `User ID ${userId}`;
      }

      if (groupId && !map[`group-${groupId}`]) {
        const res = await app.get(`user/api/client-api/group/${groupId}`).catch(() => null);
        map[`group-${groupId}`] = res?.data?.data?.groupName || `Group ID ${groupId}`;
      }
    }

    setMetaMap(map);
    setLoadingMeta(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const results = {};
      let allEnrollments = [];

      if (activeFilter) {
        // Fetch all active enrollments first
        const allRes = await app.get('user/api/client-api/enrollment/dashboard/active');
        if (allRes?.data?.status === 'SUCCESS') {
          const allData = allRes.data.data;

          // Filter out completed enrollments (cycles = 0 and inactive)
          const activeOnly = allData.filter(item => !(item.numberOfCycles === 0 && !item.isActive));
          allEnrollments = [...activeOnly];

          // Categorize data based on logic
          results.upcoming = activeOnly.filter(item => {
            const nextDate = dayjs(item.nextEnrollmentDate);
            const now = dayjs();
            return nextDate.isAfter(now) && !item.lastProcessedAt;
          });

          results.ongoing = activeOnly.filter(item => {
            return item.lastProcessedAt;
          });

          // Get completed from inactive endpoint
          const inactiveRes = await app.get('user/api/client-api/enrollment/dashboard/inactive');
          if (inactiveRes?.data?.status === 'SUCCESS') {
            const completedData = inactiveRes.data.data.filter(item => item.numberOfCycles === 0);
            results.completed = completedData;
            allEnrollments = [...allEnrollments, ...completedData];
          }
        }

        // Set all data for grouping
        results.all = allEnrollments;
      } else {
        // Fetch inactive enrollments but exclude completed ones
        const res = await app.get('user/api/client-api/enrollment/dashboard/inactive');
        if (res?.data?.status === 'SUCCESS') {
          results.inactive = res.data.data.filter(item => item.numberOfCycles !== 0);
          allEnrollments = [...results.inactive];
        } else {
          message.error(res?.data?.message || 'Failed to fetch inactive enrollments');
        }
      }

      setDataSets(results);

      // Fetch meta data for names
      if (allEnrollments.length > 0) {
        await fetchMeta(allEnrollments);
      }
    } catch (err) {
      message.error(err?.message || 'Error fetching dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeFilter]);


  useEffect(() => {
    setHeader(<div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', marginBottom: 16 }}>
      {/* <Title level={4}>Enrollment Frequency Dashboard</Title> */}
      <Space  >
        <CustomButton icon={<CalendarOutlined />} onClick={() => setCalendarVisible(true)}>
          Calendar
        </CustomButton>
        <CustomButton type="primary" onClick={handleAssignFrequency}>
          Assign Frequency
        </CustomButton>
      </Space>
    </div>)
  }, [])
  const handleTrigger = async (group) => {
    try {
      for (const item of group) {
        await app.post(`user/api/client-api/enrollment/frequency/${item.id}/trigger-now`);
      }
      message.success('Frequency triggered successfully');
      fetchData();
    } catch (err) {
      message.error(err?.message || 'Error triggering frequency');
    }
  };

  const handleDelete = async (group) => {
    try {
      for (const item of group) {
        await app.delete(`user/api/client-api/enrollment/frequency/${item.id}`);
      }
      message.success('Frequency deactivated');
      fetchData();
    } catch (err) {
      message.error(err?.message || 'Error deleting frequency');
    }
  };

  const handleReactivate = async (group) => {
    try {
      for (const item of group) {
        await app.put(`user/api/client-api/enrollment/frequency/${item.id}/reactivate`);
      }
      message.success('Frequency reactivated');
      fetchData();
    } catch (err) {
      message.error(err?.message || 'Error reactivating frequency');
    }
  };

  const handleEdit = (group) => {
    setEditRecord({ ...group[0], group });
    setEditModalVisible(true);
  };

  const handleViewDetails = (group) => {
    const sample = group[0];
    const details = {};

    if (sample.bundleId) {
      const courseIds = group
        .map(e => e.courseId)
        .filter((value, index, self) => self.indexOf(value) === index);
      details.courses = courseIds.map(id => ({
        courseId: id,
        title: metaMap[id] || `Course ID ${id}`
      }));
    }

    if (sample.groupId) {
      const userIds = group
        .map(e => e.userId)
        .filter((value, index, self) => self.indexOf(value) === index);
      details.members = userIds.map(id => ({
        userId: id,
        name: metaMap[`user-${id}`] || `User ID ${id}`
      }));
    }

    details.bundleId = sample.bundleId || null;
    details.groupId = sample.groupId || null;

    setDetailsData(details);
    setDetailsModalVisible(true);
  };

  const getDisplayName = (e) => {
    if (e.bundleId) return metaMap[e.bundleId] || `Bundle ID ${e.bundleId}`;
    if (e.courseId) return metaMap[e.courseId] || `Course ID ${e.courseId}`;
    return 'Unknown Content';
  };

  const getAssignedTo = (e) => {
    if (e.groupId) return `Group - ${metaMap[`group-${e.groupId}`] || `Group ID ${e.groupId}`}`;
    if (e.userId) return `User - ${metaMap[`user-${e.userId}`] || `User ID ${e.userId}`}`;
    return 'Unassigned';
  };

  const getCalendarData = () => {
    const allEnrollments = [
      ...(dataSets.upcoming || []),
      ...(dataSets.completed || []),
      ...(dataSets.ongoing || [])
    ];

    // Group enrollments by the same key as table grouping
    const groupedData = {};
    allEnrollments.forEach(item => {
      const key = getGroupKey(item);
      if (!groupedData[key]) groupedData[key] = [];
      groupedData[key].push(item);
    });

    // Create calendar data with grouped items (one per group)
    return Object.entries(groupedData).reduce((acc, [groupKey, group]) => {
      const sample = group[0]; // Use first item as representative
      if (!sample.nextEnrollmentDate) return acc;
      const date = dayjs(sample.nextEnrollmentDate);
      if (!date.isValid()) return acc;

      const key = date.format('YYYY-MM-DD');
      acc[key] = acc[key] ? [...acc[key], { ...sample, groupKey, groupSize: group.length }] : [{ ...sample, groupKey, groupSize: group.length }];
      return acc;
    }, {});
  };

  const getGroupedTableData = (tabKey = 'all') => {
    let dataToGroup;

    if (activeFilter) {
      // For active view, use specific datasets based on tab
      switch (tabKey) {
        case 'upcoming':
          dataToGroup = dataSets.upcoming || [];
          break;
        case 'ongoing':
          dataToGroup = dataSets.ongoing || [];
          break;
        case 'completed':
          dataToGroup = dataSets.completed || [];
          break;
        default:
          dataToGroup = dataSets.all || [];
      }
    } else {
      // For inactive view, always use inactive data
      dataToGroup = dataSets.inactive || [];
    }

    const groupedData = {};

    dataToGroup.forEach(item => {
      const key = getGroupKey(item);
      if (!groupedData[key]) groupedData[key] = [];
      groupedData[key].push(item);
    });

    return Object.entries(groupedData).map(([key, group]) => {
      const sample = group[0];
      return {
        key,
        group,
        contentType: sample.bundleId ? 'Bundle' : 'Course',
        contentName: getDisplayName(sample),
        assignedTo: getAssignedTo(sample),
        frequencyInMonths: sample.frequencyInMonths,
        numberOfCycles: sample.numberOfCycles,
        nextEnrollmentDate: sample.nextEnrollmentDate,
        lastProcessedAt: sample.lastProcessedAt,
        createdAt: sample.createdAt,
        isActive: sample.isActive,
        status: sample.numberOfCycles === 0 && !sample.isActive ? 'Completed' :
          (!sample.lastProcessedAt && dayjs(sample.nextEnrollmentDate).isAfter(dayjs()) ? 'Upcoming' : 'Ongoing'),
        hasDetails: sample.bundleId || sample.groupId
      };
    });
  };

  const columns = [
    {
      title: 'Content',
      dataIndex: 'contentName',
      key: 'contentName',
      render: (text, record) => (
        <div>
          <Text strong>{record.contentType}: {text}</Text>
          <br />
          <Text type="secondary">{record.assignedTo}</Text>
        </div>
      )
    },
    {
      title: 'Frequency (Months)',
      dataIndex: 'frequencyInMonths',
      key: 'frequencyInMonths'
    },
    {
      title: 'Cycles',
      dataIndex: 'numberOfCycles',
      key: 'numberOfCycles',
      render: (val, record) => record.status === 'Completed' ? 'Completed' : (val || '∞')
    },
    {
      title: 'Next Enrollment',
      dataIndex: 'nextEnrollmentDate',
      key: 'nextEnrollmentDate',
      render: (val, record) => {
        // Don't show next enrollment date for completed items
        if (record.status === 'Completed') return 'N/A';
        return val ? dayjs(val).format('DD MMM YYYY, h:mm A') : '—';
      }
    },
    {
      title: 'Last Processed',
      dataIndex: 'lastProcessedAt',
      key: 'lastProcessedAt',
      render: val => val ? dayjs(val).format('DD MMM YYYY, h:mm A') : '—'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        const color = status === 'Completed' ? 'green' :
          status === 'Upcoming' ? 'cyan' : 'orange';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: val => (val ? '✅' : '❌')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.hasDetails && (
            <Tooltip title="View Details">
              <Button
                icon={<EyeOutlined />}
                onClick={() => handleViewDetails(record.group)}
                size="small"
              />
            </Tooltip>
          )}
          {activeFilter ? (
            <>
              <Tooltip title="Edit Frequency">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record.group)}
                  size="small"
                />
              </Tooltip>
              <Tooltip title="Trigger Now">
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={() => handleTrigger(record.group)}
                  size="small"
                />
              </Tooltip>
              <Tooltip title="Delete Frequency">
                <Popconfirm
                  title="Are you sure you want to deactivate this frequency?"
                  onConfirm={() => handleDelete(record.group)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} danger size="small" />
                </Popconfirm>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Reactivate Frequency">
              <Button
                type="primary"
                onClick={() => handleReactivate(record.group)}
                size="small"
              >
                Reactivate
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];
  const selectionColumns = [
    {
      title: (
        <Checkbox
          indeterminate={selectedGroups.length > 0 && selectedGroups.length < availableEnrollments.length}
          checked={selectedGroups.length === availableEnrollments.length && availableEnrollments.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          Select All
        </Checkbox>
      ),
      key: 'select',
      width: 120,
      render: (_, record) => (
        <Checkbox
          checked={selectedGroups.includes(record.key)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedGroups(prev => [...prev, record.key]);
            } else {
              setSelectedGroups(prev => prev.filter(key => key !== record.key));
            }
          }}
        />
      )
    },
    {
      title: 'Content',
      dataIndex: 'contentName',
      key: 'contentName',
      render: (text, record) => (
        <div>
          <Text strong>{record.contentType}: {text}</Text>
          <br />
          <Text type="secondary">{record.assignedTo}</Text>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        record.hasDetails && (
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleSelectionViewDetails(record.group)}
              size="small"
            />
          </Tooltip>
        )
      )
    }
  ];

  const handleAssignFrequency = async () => {
    try {
      const res = await app.get('user/api/client-api/enrollment/grouped-for-frequency');
      if (res?.data?.status === 'SUCCESS') {
        const groups = res.data.data;

        // Flatten all enrollments from all groups
        const allEnrollments = groups.flatMap(group => group.enrollments);

        // Create grouping logic based on your data structure
        const createGroupKey = (enrollment) => {
          if (enrollment.bundleId && enrollment.groupId) {
            return `bundle-${enrollment.bundleId}-group-${enrollment.groupId}`;
          }
          if (enrollment.bundleId && enrollment.userId && !enrollment.groupId) {
            return `bundle-${enrollment.bundleId}-user-${enrollment.userId}`;
          }
          if (enrollment.courseId && enrollment.groupId) {
            return `course-${enrollment.courseId}-group-${enrollment.groupId}`;
          }
          if (enrollment.courseId && enrollment.userId && !enrollment.groupId) {
            return `course-${enrollment.courseId}-user-${enrollment.userId}`;
          }
          return `enrollment-${enrollment.enrollmentId}`;
        };

        // Group enrollments by the key
        const groupedData = {};
        allEnrollments.forEach(enrollment => {
          const key = createGroupKey(enrollment);
          if (!groupedData[key]) {
            groupedData[key] = [];
          }
          groupedData[key].push(enrollment);
        });

        // Convert grouped data to table format
        const enrollmentGroups = Object.entries(groupedData).map(([key, group]) => {
          const sample = group[0];

          const getDisplayName = (e) => {
            if (e.bundleId) return e.bundleName || `Bundle ID ${e.bundleId}`;
            if (e.courseId) return e.courseName || `Course ID ${e.courseId}`;
            return 'Unknown Content';
          };

          const getAssignedTo = (e) => {
            if (e.groupId) {
              // For group assignments, show all users in the group
              const userNames = group.map(enrollment => enrollment.userName).filter(name => name);
              const uniqueNames = [...new Set(userNames)];
              return `Group - ${e.groupName || `Group ID ${e.groupId}`} (${uniqueNames.length} users)`;
            }
            if (e.userId) return `User - ${e.userName || `User ID ${e.userId}`}`;
            return 'Unassigned';
          };

          return {
            key,
            group,
            contentType: sample.bundleId ? 'Bundle' : 'Course',
            contentName: getDisplayName(sample),
            assignedTo: getAssignedTo(sample),
            hasDetails: sample.bundleId || sample.groupId,
            enrollmentCount: group.length
          };
        });

        console.log('Grouped enrollments:', enrollmentGroups);
        console.log('Original groups from API:', groups);
        setAvailableEnrollments(enrollmentGroups);
        setSelectionModalVisible(true);
      } else {
        message.error(res?.data?.message || 'Failed to fetch grouped enrollments');
      }
    } catch (err) {
      console.error('Error in handleAssignFrequency:', err);
      message.error(err?.message || 'Error fetching grouped enrollments');
    }
  };

  const handleProceedToFrequency = () => {
    const selectedEnrollments = selectedGroups.flatMap(groupKey => {
      const group = availableEnrollments.find(g => g.key === groupKey);
      return group ? group.group : [];
    });

    if (selectedEnrollments.length === 0) {
      message.warning('Please select at least one group');
      return;
    }

    setEditRecord({ group: selectedEnrollments });
    setSelectionModalVisible(false);
    setFrequencyModalVisible(true);
    setSelectedGroups([]);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedGroups(availableEnrollments.map(g => g.key));
    } else {
      setSelectedGroups([]);
    }
  };

  const handleSelectionViewDetails = (group) => {
    const sample = group[0];
    const details = {};

    if (sample.bundleId) {
      const courseIds = group
        .map(e => ({ courseId: e.courseId, title: e.courseName }))
        .filter((value, index, self) =>
          index === self.findIndex(v => v.courseId === value.courseId)
        );
      details.courses = courseIds;
    }

    if (sample.groupId) {
      const members = group
        .map(e => ({ userId: e.userId, name: e.userName }))
        .filter((value, index, self) =>
          index === self.findIndex(v => v.userId === value.userId)
        );
      details.members = members;
    }

    details.bundleId = sample.bundleId || null;
    details.groupId = sample.groupId || null;

    setDetailsData(details);
    setDetailsModalVisible(true);
  };

  const getFilteredEnrollments = () => {
    if (!availableEnrollments) return [];

    return availableEnrollments.filter(enrollment => {
      const sample = enrollment.group[0];

      // Check assignment type (user vs group)
      const assignmentMatch = assignmentFilter === 'users'
        ? (sample.userId && !sample.groupId) // Individual user assignments
        : sample.groupId; // Group assignments

      // Check content type (course vs bundle)
      const contentMatch = contentFilter === 'courses'
        ? (sample.courseId && !sample.bundleId) // Individual courses
        : sample.bundleId; // Bundle assignments

      return assignmentMatch && contentMatch;
    });
  };


  const tablist = [
    {
      key: '1', label: 'All', children:
        <>{loading || loadingMeta ? (
          <Spin tip="Loading..." />
        ) : (
          <Table
            dataSource={getGroupedTableData()}
            columns={columns}
            rowKey="key"
            pagination={{ pageSize: 10 }}
          />
        )}
        </>
    },
    {
      key: '2', label: 'Upcoming',
      children: <>
        {loading || loadingMeta ? (
          <Spin tip="Loading..." />
        ) : (
          <Table
            dataSource={getGroupedTableData('upcoming')}
            columns={columns}
            rowKey="key"
            pagination={{ pageSize: 10 }}
          />
        )}
      </>
    },
    {
      key: '3', label: 'Ongoing', children:
        <>
          {loading || loadingMeta ? (
            <Spin tip="Loading..." />
          ) : (
            <Table
              dataSource={getGroupedTableData('ongoing')}
              columns={columns}
              rowKey="key"
              pagination={{ pageSize: 10 }}
            />
          )}
        </>
    },
    {
      key: '4', label: 'Completed', children:
        <>
          {loading || loadingMeta ? (
            <Spin tip="Loading..." />
          ) : (
            <Table
              dataSource={getGroupedTableData('completed')}
              columns={columns}
              rowKey="key"
              pagination={{ pageSize: 10 }}
            />
          )}
        </>
    },

  ];

  return (
    <div style={{ padding: '0px 0px 0px 10px', margin: '0 auto' }}>


      <Radio.Group
        value={activeFilter}
        onChange={e => setActiveFilter(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value={true}>Active</Radio.Button>
        <Radio.Button value={false}>Inactive</Radio.Button>
      </Radio.Group>

      {/* <Tabs defaultActiveKey="all">
        <TabPane tab="All" key="all">
          {loading || loadingMeta ? (
            <Spin tip="Loading..." />
          ) : (
            <Table
              dataSource={getGroupedTableData()}
              columns={columns}
              rowKey="key"
              pagination={{ pageSize: 10 }}
            />
          )}
        </TabPane>

        <TabPane tab="Upcoming" key="upcoming">
          {loading || loadingMeta ? (
            <Spin tip="Loading..." />
          ) : (
            <Table
              dataSource={getGroupedTableData('upcoming')}
              columns={columns}
              rowKey="key"
              pagination={{ pageSize: 10 }}
            />
          )}
        </TabPane>

        <TabPane tab="Ongoing" key="ongoing">
          {loading || loadingMeta ? (
            <Spin tip="Loading..." />
          ) : (
            <Table
              dataSource={getGroupedTableData('ongoing')}
              columns={columns}
              rowKey="key"
              pagination={{ pageSize: 10 }}
            />
          )}
        </TabPane>

        <TabPane tab="Completed" key="completed">
          {loading || loadingMeta ? (
            <Spin tip="Loading..." />
          ) : (
            <Table
              dataSource={getGroupedTableData('completed')}
              columns={columns}
              rowKey="key"
              pagination={{ pageSize: 10 }}
            />
          )}
        </TabPane>
      </Tabs> */}



      <CustomTab entries={tablist} />



      {/* Calendar Modal */}
      <Modal
        title="Enrollment Calendar"
        visible={calendarVisible}
        onCancel={() => setCalendarVisible(false)}
        footer={null}
        width={700}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {/* Color Key */}
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <Text strong style={{ marginBottom: 8, display: 'block' }}>Color Key:</Text>
          <Space wrap>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#40e0d0', borderRadius: 2 }}></div>
              <Text>Upcoming</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#52c41a', borderRadius: 2 }}></div>
              <Text>Completed</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#fa8c16', borderRadius: 2 }}></div>
              <Text>Ongoing</Text>
            </div>
          </Space>
        </div>

        <Calendar
          fullscreen={false}
          dateCellRender={(date) => {
            const formatted = date.format('YYYY-MM-DD');
            const items = getCalendarData()[formatted] || [];

            if (items.length === 0) return null;

            // Status helpers
            const isCompleted = item => item.numberOfCycles === 0 && !item.isActive;
            const isUpcoming = item => !item.lastProcessedAt && dayjs(item.nextEnrollmentDate).isAfter(dayjs());
            const isOngoing = item => item.lastProcessedAt;

            // Unique statuses present that day
            const statuses = items.map(item => {
              if (isCompleted(item)) return 'completed';
              if (isUpcoming(item)) return 'upcoming';
              if (isOngoing(item)) return 'ongoing';
              return null;
            }).filter(Boolean);

            const uniqueStatuses = Array.from(new Set(statuses));
            if (uniqueStatuses.length === 0) return null;

            // Map status -> color
            const colorOf = (status) => {
              if (status === 'completed') return '#52c41a';
              if (status === 'upcoming') return '#40e0d0'; // turquoise
              if (status === 'ongoing') return '#fa8c16';
              return '#d9d9d9';
            };

            return (
              <Tooltip
                title={items.map(item => (
                  <div key={item.groupKey}>
                    {getDisplayName(item)} → {getAssignedTo(item)}
                  </div>
                ))}
              >
                <div style={{ marginTop: 4, display: 'flex', justifyContent: 'center', gap: 4 }}>
                  {uniqueStatuses.map(status => (
                    <div
                      key={status}
                      style={{
                        width: 20,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: colorOf(status),
                      }}
                    />
                  ))}
                </div>
              </Tooltip>
            );
          }}
        />
      </Modal>



      {/* Details Modal */}
      <Modal
        title="Assignment Details"
        visible={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        {detailsData?.courses && (
          <>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              📦 Courses in Bundle:
            </Text>
            <ul style={{ marginBottom: 24 }}>
              {detailsData.courses.map(c => (
                <li key={c.courseId}>{c.title}</li>
              ))}
            </ul>
          </>
        )}

        {detailsData?.members && (
          <>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              👥 Group Members:
            </Text>
            <ul>
              {detailsData.members.map(m => (
                <li key={m.userId}>{m.name}</li>
              ))}
            </ul>
          </>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Frequency"
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={async () => {
          try {
            const payload = {
              frequencyInMonths: editRecord.frequencyInMonths,
              numberOfCycles: editRecord.numberOfCycles
            };

            // Update all items in the group
            for (const item of editRecord.group) {
              await app.put(`user/api/client-api/enrollment/frequency/${item.id}`, payload);
            }

            message.success('Frequency updated');
            setEditModalVisible(false);
            fetchData();
          } catch (err) {
            message.error(err?.message || 'Error updating frequency');
          }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <label>Frequency (Months)</label>
          <input
            type="number"
            value={editRecord?.frequencyInMonths || ''}
            onChange={e =>
              setEditRecord(prev => ({ ...prev, frequencyInMonths: Number(e.target.value) }))
            }
          />
          <label>Number of Cycles</label>
          <input
            type="number"
            value={editRecord?.numberOfCycles ?? ''}
            onChange={e =>
              setEditRecord(prev => ({
                ...prev,
                numberOfCycles: e.target.value ? Number(e.target.value) : null
              }))
            }
          />
        </Space>
      </Modal>

      {/* Selection Modal */}
      <Modal
        title="Select Enrollments for Frequency Assignment"
        visible={selectionModalVisible}
        onCancel={() => {
          setSelectionModalVisible(false);
          setSelectedGroups([]);
          setAssignmentFilter('users'); // Reset filters
          setContentFilter('courses');
        }}
        footer={
          <Space>
            <Button onClick={() => {
              setSelectionModalVisible(false);
              setSelectedGroups([]);
              setAssignmentFilter('users'); // Reset filters
              setContentFilter('courses');
            }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleProceedToFrequency}
              disabled={selectedGroups.length === 0}
            >
              Proceed to Set Frequency ({selectedGroups.length} selected)
            </Button>
          </Space>
        }
        width={800}
      >
        {/* Filter Groups */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ marginRight: 12 }}>Assignment Type:</Text>
            <Radio.Group
              value={assignmentFilter}
              onChange={e => {
                setAssignmentFilter(e.target.value);
                setSelectedGroups([]); // Clear selections when filter changes
              }}
              buttonStyle="solid"
            >
              <Radio.Button value="users">Users</Radio.Button>
              <Radio.Button value="groups">Groups</Radio.Button>
            </Radio.Group>
          </div>

          <div>
            <Text strong style={{ marginRight: 12 }}>Content Type:</Text>
            <Radio.Group
              value={contentFilter}
              onChange={e => {
                setContentFilter(e.target.value);
                setSelectedGroups([]); // Clear selections when filter changes
              }}
              buttonStyle="solid"
            >
              <Radio.Button value="courses">Courses</Radio.Button>
              <Radio.Button value="bundles">Bundles</Radio.Button>
            </Radio.Group>
          </div>
        </div>

        <Table
          dataSource={getFilteredEnrollments()}
          columns={selectionColumns}
          rowKey="key"
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      {/* Frequency Modal */}
      {frequencyModalVisible && editRecord && (
        <FrequencyModal
          enrollments={editRecord.group}
          onCancel={() => {
            setFrequencyModalVisible(false);
            setEditRecord(null);
            fetchData(); // Refresh dashboard data
          }}
        />
      )}
    </div>
  );
};

export default AdminHOC(FrequencyDashboard);